import { FC, memo, ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import classnames from 'classnames';

import IconColor from 'bloko/blocks/icon/colors.d';
import loggingNotValidIcon from 'bloko/blocks/icon/loggingNotValidIcon';
import makeIconComponent from 'bloko/blocks/icon/variants/icon/Switcher';
import {
    IconScale,
    IconAppearance,
    IconShape,
    IconKind,
    IconView,
    IconInvariants,
    IconEnclosed,
    IconIntersect,
    IconCrossed,
    IconReduced,
} from 'bloko/blocks/icon/variants/icon/types.d';

import styles from 'bloko/blocks/icon/icon.less';

interface IconProps extends ComponentPropsWithoutRef<'svg'> {
    initial?: IconColor;
    highlighted?: IconColor;
    source?: string;
    scale?: IconScale;
    kind?: IconKind;
    appearance?: IconAppearance;
    shape?: IconShape;
    enclosed?: IconEnclosed;
    intersect?: IconIntersect;
    crossed?: IconCrossed;
    reduced?: IconReduced;
}
type ComponentProps<T> = {
    [Prop in keyof T]: T[Prop];
};
export type IconPropsByView = IconProps & ComponentProps<IconInvariants>;

const Icon: FC<IconPropsByView & PropsWithChildren> = ({
    view,
    initial,
    highlighted,
    scale = IconScale.Small,
    appearance,
    shape,
    kind,
    enclosed,
    intersect,
    crossed,
    reduced,
    source,
    ...iconProps
}) => {
    const classNames = {
        [styles['bloko-icon_loader']]: view === IconView.Loader,
    };

    if (initial) {
        classNames[styles[`bloko-icon_initial-${initial}`]] = !!initial;
    }

    if (highlighted) {
        classNames[styles[`bloko-icon_highlighted-${highlighted}`]] = !!highlighted;
    }

    const props = {
        ...iconProps,
        className: classnames(styles['bloko-icon'], classNames),
    };

    const iconSwitcherProps = {
        scale,
        appearance,
        shape,
        kind,
        enclosed,
        intersect,
        crossed,
        reduced,
        view,
    };

    const Icon = makeIconComponent(iconSwitcherProps);

    if (Icon) {
        return <Icon {...props} />;
    }

    loggingNotValidIcon(iconSwitcherProps);

    return null;
};

export default memo(Icon);
